<template>
  <div class="geo-searchbox">
    <v-text-field
      autocomplete="off"
      label="Find Location"
      prepend-icon="search"
      :loading="loading"
      v-model="val"
      @click:prepend="searchButtonHandler"
      @keydown.native.down.prevent="down"
      @keydown.native.enter.prevent.stop="hit"
      @keydown.native.esc="onEsc"
      @keydown.native.up.prevent="up"
      :error-messages="errorMessages"
    />
    <transition appear name="slide-y-transition">
      <v-card class="dropdown-menu elevation-4" ref="dropdown" v-if="showDropdown">
        <v-list
          v-for="gt in geoTypes"
          :key="gt.type" subheader
          v-if="items[gt.type].length"
        >
          <v-subheader>{{ geoTitle(gt) }}</v-subheader>
          <v-list-tile
            v-for="(item, i) in items[gt.type]"
            :key="i"
            :class="{active: isActive(i, gt.type)}"
            @click=""
            ripple
            @mouseenter="setActive(i, gt.type)"
            @mousedown.prevent.stop='hit'
          >
            <v-list-tile-content>
              <v-list-tile-title>{{ item.name }}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </v-list>
      </v-card>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import capitalize from 'lodash/capitalize'

const DELAY = 350
const geoTypes = [{type: 'cities'}, {type: 'counties'}, {type: 'cbsas', name: 'Metros'}, {type: 'states'}]

// delayer: set a function that execute after a delay
// @params (function, delay_prop or value, default_value)
function delayer (fn, varTimer, ifNaN = 100) {
  function toInt (el) { return /^[0-9]+$/.test(el) ? Number(el) || 1 : null }
  var timerId
  return function (...args) {
    if (timerId) clearTimeout(timerId)
    timerId = setTimeout(() => {
      fn.apply(this, args)
    }, toInt(varTimer) || toInt(this[varTimer]) || ifNaN)
  }
}

export default {
  mounted () {
    this.$el.querySelector('input').focus()
  },

  props: {
    onSelect: {
      type: Function,
      required: true
    }
  },

  data () {
    return {
      geoTypes: geoTypes,
      showDropdown: false,
      current: { geoType: 'cities', index: 0 }, // 'current' needs to be among the data properties to make sure changes to it will trigger re-rendering the search box
      items: { cities: [], counties: [], cbsas: [], states: [] },
      val: '',
      showNoResultsMsg: false,
      loading: false
    }
  },

  watch: {
    val (val, old) {
      if (val !== old) {
        this.showNoResultsMsg = false
        this.__update()
      }
    }
  },

  computed: {
    errorMessages () {
      return this.showNoResultsMsg ? [`Your search - ${this.val} - did not match any location.`] : []
    }
  },

  methods: {
    searchButtonHandler () {
      if (this.showDropdown) {
        if (!this.hit()) {
          this.$el.querySelector('input').focus()
        }
      } else {
        this.$el.querySelector('input').focus()
        if (!this.resultsListEmpty()) {
          this.showDropdown = true
        }
      }
    },
    setItems (data) {
      const res = data.results
      if (res) {
        this.items.cities = res.cities
        this.items.counties = res.counties
        this.items.cbsas = res.cbsas
        this.items.states = res.states
      }

      geoTypes.some((gt) => {
        if (this.items[gt.type].length) {
          this.current = {
            geoType: gt.type,
            index: 0
          }
          return true
        }
      })

      this.showDropdown = !this.resultsListEmpty()
      this.showNoResultsMsg = this.resultsListEmpty()
    },

    onEsc (e) {
      if (this.showDropdown) {
        e.stopPropagation() // do not stop propagation when the drop down is not shown. Instead hide the overlay (done by parent component)
      }
      this.showDropdown = false
    },

    reset () {
      this.val = null
      this.items = { cities: [], counties: [], cbsas: [], states: [] }
      this.showDropdown = false
    },

    resultsListEmpty () {
      const items = this.items
      return items.cities.length + items.counties.length + items.cbsas.length + items.states.length === 0
    },

    setActive (index, geoType) {
      this.current = {geoType: geoType, index: index}
    },

    isActive (index, geoType) {
      const current = this.current
      return current && current.index === index && current.geoType === geoType
    },

    geoTitle (gt) {
      return capitalize(gt.name ? gt.name : gt.type)
    },

    hit () {
      if (this.showDropdown) {
        const current = this.current
        const items = this.items
        if (current && items && items[current.geoType][current.index]) {
          this.onSelect(items[current.geoType][current.index])
          return true
        }
      } else if (!this.resultsListEmpty()) {
        this.showDropdown = true
      }
      return false
    },

    down () {
      if (!this.resultsListEmpty()) {
        if (this.showDropdown) {
          const _this = this
          geoTypes.some((gt, i) => {
            if (gt.type === _this.current.geoType) {
              if (_this.items[gt.type][_this.current.index + 1]) {
                _this.current.index++
              } else {
                let geoTypeIndex = 0
                for (let j = i + 1; j < 4; j++) {
                  const items = _this.items[geoTypes[j].type]
                  if (items && items.length > 0) {
                    geoTypeIndex = j
                    break
                  }
                }
                _this.current = {
                  geoType: geoTypes[geoTypeIndex].type,
                  index: 0
                }
              }
              return true
            }
          })
        } else {
          this.showDropdown = true
        }
      }
    },

    up () {
      if (!this.resultsListEmpty()) {
        if (this.showDropdown) {
          const _this = this
          geoTypes.some((gt, i) => {
            if (gt.type === this.current.geoType) {
              if (this.current.index - 1 >= 0) {
                this.current.index--
              } else {
                let geoTypeIndex = 3
                for (let j = i - 1; j >= 0; j--) {
                  // TODO this logic is limping because if this loop fails to set geoTypeIndex
                  // it'll be set to 3 (type: states) although the current set of search results
                  // may have no states causing the active indicator to be lost for the period of a ket event.
                  // Most likely the down() function has a similar problem.
                  const items = _this.items[geoTypes[j].type]
                  if (items && items.length > 0) {
                    geoTypeIndex = j
                    break
                  }
                }
                const geoType = geoTypes[geoTypeIndex].type
                this.current = {
                  geoType: geoType,
                  index: this.items[geoType].length - 1
                }
              }
              return true
            }
          })
        } else {
          this.showDropdown = true
        }
      }
    },

    clickHandler (e) {
      if (e.target === this.$el.querySelector('input')) {
        if (!this.resultsListEmpty()) {
          this.showDropdown = true
        }
      } else {
        this.showDropdown = false
      }
    }
  },

  destroyed () {
    document.removeEventListener('click', this.clickHandler)
  },

  created () {
    document.addEventListener('click', this.clickHandler)

    this.__update = delayer(function () {
      if (!this.val) {
        this.reset()
        return
      }
      this.loading = true
      axios.get(`/g_api/search_geo?q=${encodeURIComponent(this.val)}`)
        .then((response) => {
          this.setItems(response.data)
          this.loading = false
        })
    }, 'delay', DELAY)
    this.__update()
  }
}
</script>

<style lang="stylus">
  @require '~vuetify/src/stylus/settings/_variables.styl'
  .geo-searchbox
    position: relative
    .dropdown-menu
      display: block
      position: absolute
      width: 100%
      z-index: 1000
      background: $colors.green.base
      margin-bottom: 20px
      .active
        a
          background: rgba(0,0,0,0.12)
</style>



// WEBPACK FOOTER //
// frontend/src/components/common/GeoSearchbox.vue